/**
 * Experimental peer-city methodology v0.1.
 * Peers are selected from structural characteristics only — never from emissions outcomes.
 */

export const PEER_METHOD_VERSION = 'v0.1-structural-distance';

export interface PeerStructuralFeatures {
  logPopulation: number | null;
  populationDensity: number | null;
  jobsToResidents: number | null;
  householdSize: number | null;
  climateZone: string | null;
  region: string | null;
  landAreaSqMi: number | null;
}

export interface PeerCandidate {
  jurisdictionId: string;
  geofips: number;
  canonicalName: string;
  incorporatedCity: boolean;
  inventoryYear: number;
  methodVersion: string;
  population: number | null;
  features: PeerStructuralFeatures;
  /** Must not be read by selectPeers. Present only so tests can prove it is ignored. */
  totalEmissionsMt?: number | null;
  perCapitaMt?: number | null;
  sectorShares?: Record<string, number>;
}

export interface PeerSelectionWeights {
  logPopulation: number;
  populationDensity: number;
  jobsToResidents: number;
  householdSize: number;
  landAreaSqMi: number;
  climateZone: number;
  region: number;
}

export const DEFAULT_PEER_WEIGHTS: PeerSelectionWeights = {
  logPopulation: 1,
  populationDensity: 1,
  jobsToResidents: 1,
  householdSize: 0.75,
  landAreaSqMi: 0.5,
  climateZone: 0.75,
  region: 0.75,
};

export const NUMERIC_FEATURE_KEYS = [
  'logPopulation',
  'populationDensity',
  'jobsToResidents',
  'householdSize',
  'landAreaSqMi',
] as const;

const CATEGORICAL_KEYS = ['climateZone', 'region'] as const;

export interface SelectedPeer {
  jurisdictionId: string;
  geofips: number;
  canonicalName: string;
  distance: number;
  featureDifferences: Record<string, number | string | null>;
}

export interface PeerSelectionResult {
  methodVersion: string;
  eligibleCount: number;
  selected: SelectedPeer[];
  suppressed: boolean;
  suppressionReason?: string;
  weights: PeerSelectionWeights;
}

const MIN_ELIGIBLE_POOL = 8;
const PEER_COUNT_MIN = 5;
const PEER_COUNT_MAX = 8;

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function mad(values: number[], med: number): number {
  const deviations = values.map((v) => Math.abs(v - med));
  return median(deviations) || 1;
}

function robustZ(value: number, med: number, scale: number): number {
  return (value - med) / (scale || 1);
}

export function eligiblePeerCohort(
  candidates: PeerCandidate[],
  target: { inventoryYear: number; methodVersion: string; geofips: number }
): PeerCandidate[] {
  return candidates.filter((c) => {
    if (c.geofips === target.geofips) return false;
    if (!c.incorporatedCity) return false;
    if (c.inventoryYear !== target.inventoryYear) return false;
    if (c.methodVersion !== target.methodVersion) return false;
    if (c.population == null || !(c.population > 0)) return false;
    const required: Array<number | null> = [
      c.features.logPopulation,
      c.features.populationDensity,
    ];
    return required.every((v) => v != null && Number.isFinite(v));
  });
}

export function selectStructuralPeers(
  target: PeerCandidate,
  candidates: PeerCandidate[],
  weights: PeerSelectionWeights = DEFAULT_PEER_WEIGHTS
): PeerSelectionResult {
  const eligible = eligiblePeerCohort(candidates, {
    inventoryYear: target.inventoryYear,
    methodVersion: target.methodVersion,
    geofips: target.geofips,
  });
  if (eligible.length < MIN_ELIGIBLE_POOL) {
    return {
      methodVersion: PEER_METHOD_VERSION,
      eligibleCount: eligible.length,
      selected: [],
      suppressed: true,
      suppressionReason: `Eligible pool ${eligible.length} is below the minimum of ${MIN_ELIGIBLE_POOL}`,
      weights,
    };
  }

  const stats: Record<string, { med: number; scale: number }> = {};
  for (const key of NUMERIC_FEATURE_KEYS) {
    const vals = eligible
      .map((c) => c.features[key])
      .filter((v): v is number => v != null && Number.isFinite(v));
    const med = vals.length ? median(vals) : 0;
    stats[key] = { med, scale: vals.length ? mad(vals, med) : 1 };
  }

  const scored = eligible.map((c) => {
    let dist = 0;
    const diffs: Record<string, number | string | null> = {};
    for (const key of NUMERIC_FEATURE_KEYS) {
      const tv = target.features[key];
      const cv = c.features[key];
      const w = weights[key];
      if (tv == null || cv == null) {
        diffs[key] = null;
        continue;
      }
      const { med, scale } = stats[key];
      const d = robustZ(tv, med, scale) - robustZ(cv, med, scale);
      dist += w * d * d;
      diffs[key] = cv - tv;
    }
    for (const key of CATEGORICAL_KEYS) {
      const tv = target.features[key];
      const cv = c.features[key];
      const w = weights[key];
      const mismatch = tv && cv ? (tv === cv ? 0 : 1) : 0.5;
      dist += w * mismatch;
      diffs[key] = tv && cv && tv !== cv ? `${cv}` : tv === cv ? 'same' : null;
    }
    return {
      jurisdictionId: c.jurisdictionId,
      geofips: c.geofips,
      canonicalName: c.canonicalName,
      distance: Math.sqrt(dist),
      featureDifferences: diffs,
    };
  });

  scored.sort((a, b) => a.distance - b.distance || a.geofips - b.geofips);
  const n = Math.min(PEER_COUNT_MAX, Math.max(PEER_COUNT_MIN, Math.min(8, scored.length)));
  return {
    methodVersion: PEER_METHOD_VERSION,
    eligibleCount: eligible.length,
    selected: scored.slice(0, n),
    suppressed: false,
    weights,
  };
}
